import { useEffect } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useParams } from 'react-router-dom'
import { RequireAuth } from '@/auth/RequireAuth'
import { ErrorBoundary } from '@/components/error-boundary'
import { AppOutlet, PublicOutlet, routePage as page } from '@/components/route-outlet'
import { markFirstPaint } from '@/lib/loader-handoff'

const LoginPage = page('login', 'LoginPage')
const SignUpPage = page('signup', 'SignUpPage')
const VerifyEmailPage = page('verifyEmail', 'VerifyEmailPage')
const McpAuthorizePage = page('mcpAuthorize', 'McpAuthorizePage')
const GarminCallbackPage = page('garminCallback', 'GarminCallbackPage')
const DashboardPage = page('dashboard', 'DashboardPage')
const HealthSleepPage = page('healthSleep', 'HealthSleepPage')
const HealthActivityPage = page('healthActivity', 'HealthActivityPage')
const HealthHeartPage = page('healthHeart', 'HealthHeartPage')
const HealthBodyPage = page('healthBody', 'HealthBodyPage')
const AudiencesOverviewPage = page('audiences', 'AudiencesOverviewPage')
const AudienceNewPage = page('audienceNew', 'AudienceNewPage')
const AudienceDetailPage = page('audienceDetail', 'AudienceDetailPage')
const ReelsOverviewPage = page('reels', 'ReelsOverviewPage')
const ReelNewPage = page('reelNew', 'ReelNewPage')
const ReelDetailPage = page('reelDetail', 'ReelDetailPage')
const SocialPostsPage = page('social', 'SocialPostsPage')
const SocialPostNewPage = page('socialNew', 'SocialPostNewPage')
const SocialPostDetailPage = page('socialDetail', 'SocialPostDetailPage')
const SignalsPage = page('signals', 'SignalsPage')
const SignalNewPage = page('signalNew', 'SignalNewPage')
const SignalSourcePage = page('signalSource', 'SignalSourcePage')
const SignalRunRedirect = page('signalSource', 'SignalRunRedirect')
const SignalItemPage = page('signalItem', 'SignalItemPage')
const PagesOverviewPage = page('pages', 'PagesOverviewPage')
const PageNewPage = page('pageNew', 'PageNewPage')
const PageViewPage = page('pageView', 'PageViewPage')
const PageEditPage = page('pageEdit', 'PageEditPage')
const SettingsAccountsPage = page('settings', 'SettingsAccountsPage')
const SettingsConnectorsPage = page('settings', 'SettingsConnectorsPage')
const SettingsSecretsPage = page('settings', 'SettingsSecretsPage')
const ApiKeysPage = page('apiKeys', 'ApiKeysPage')

export default function App() {
  // After the first frame, a loader that appears fades in; the one ON the first
  // frame is simply there (lib/loader-handoff.ts).
  useEffect(markFirstPaint, [])
  return (
    // OUTSIDE the router, deliberately. A throw inside a route unmounts the
    // tree it lives in; a boundary within that tree goes with it. Out here it
    // survives whatever the routed page did, which is the only position from
    // which it can offer anything at all.
    <ErrorBoundary>
      <BrowserRouter>
        <Routes>
          <Route element={<PublicOutlet />}>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignUpPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            {/* Outside RequireAuth on purpose: AuthKit sends people here BEFORE they
            are signed in, and the page renders the login form itself. */}
            <Route path="/mcp/authorize" element={<McpAuthorizePage />} />
            {/* Same reason, and one more: Garmin's consent redirect lands here
            carrying a single-use verifier in the query string. RequireAuth would
            bounce to /login, and anything that navigates loses the query string
            and with it the consent. The page renders the login form in place. */}
            <Route path="/garmin" element={<GarminCallbackPage />} />
          </Route>
          <Route element={<RequireAuth />}>
            <Route element={<AppOutlet />}>
              <Route path="/" element={<DashboardPage />} />
              <Route path="/health/sleep" element={<HealthSleepPage />} />
              <Route path="/health/activity" element={<HealthActivityPage />} />
              <Route path="/health/heart" element={<HealthHeartPage />} />
              <Route path="/health/body" element={<HealthBodyPage />} />
              <Route path="/audiences" element={<AudiencesOverviewPage />} />
              <Route path="/audiences/new" element={<AudienceNewPage />} />
              <Route path="/audiences/:id" element={<AudienceDetailPage />} />
              {/* The old address, kept as a redirect: bookmarks and shared links
                  from before the rename still land. */}
              <Route path="/analyses" element={<Navigate to="/audiences" replace />} />
              <Route path="/analyses/new" element={<Navigate to="/audiences/new" replace />} />
              <Route path="/analyses/:id" element={<LegacyAudienceRedirect />} />
              <Route path="/reels" element={<ReelsOverviewPage />} />
              <Route path="/reels/new" element={<ReelNewPage />} />
              <Route path="/reels/:id" element={<ReelDetailPage />} />
              <Route path="/social" element={<SocialPostsPage />} />
              <Route path="/social/new" element={<SocialPostNewPage />} />
              <Route path="/social/:id" element={<SocialPostDetailPage />} />
              <Route path="/signals" element={<SignalsPage />} />
              <Route path="/signals/new" element={<SignalNewPage />} />
              <Route path="/signals/sources/:sourceId" element={<SignalSourcePage />} />
              <Route path="/signals/sources/:sourceId/items/:itemId" element={<SignalItemPage />} />
              {/* Links from before sources: a run id opens its source. */}
              <Route path="/signals/:id" element={<SignalRunRedirect />} />
              <Route path="/signals/:id/items/:itemId" element={<SignalItemPage />} />
              <Route path="/pages" element={<PagesOverviewPage />} />
              <Route path="/pages/new" element={<PageNewPage />} />
              {/* create | view | edit are separate doors: /new creates, /:slug
                reads the rendered end view, /:slug/edit changes it. */}
              <Route path="/pages/:slug" element={<PageViewPage />} />
              <Route path="/pages/:slug/edit" element={<PageEditPage />} />
              {/* Settings is a section; its bare address opens the first page. */}
              <Route path="/settings" element={<Navigate to="/settings/accounts" replace />} />
              <Route path="/settings/accounts" element={<SettingsAccountsPage />} />
              <Route path="/settings/agentic-access" element={<ApiKeysPage />} />
              <Route path="/settings/connectors" element={<SettingsConnectorsPage />} />
              <Route path="/settings/secrets" element={<SettingsSecretsPage />} />
              {/* Agent keys moved into Settings → Agentic access; the old address still lands. */}
              <Route
                path="/api-keys"
                element={<Navigate to="/settings/agentic-access" replace />}
              />
            </Route>
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  )
}

/** /analyses/:id → /audiences/:id, keeping the id. */
function LegacyAudienceRedirect() {
  const { id } = useParams()
  return <Navigate to={`/audiences/${id}`} replace />
}
